import { WORKFLOW_NODE_TYPES } from 'modules/workflows/constants';
import { WorkflowState } from 'modules/workflows/ducks';
import {
  BackendEdge,
  BackendNoteNode,
  BackendWFNode,
  Channel,
  Edge,
  Node,
  NoteNode,
  WFNode,
} from 'modules/workflows/types';
import {
  parseBackendNodes,
  parseClonedNodes,
  parseEdge,
  parseNodeForSaving,
} from 'modules/workflows/utils';
import { EdgeModel, NodeId } from 'ui/workflows/flowchart/types';
import { TilesetData } from './types';

/**
 * Determines how much to shift the tiles from their starting coordinates
 */
export const getTilesetShift = (
  tiles: Array<BackendWFNode | BackendNoteNode> = [],
  { startX = 100, startY = 100 }: { startX?: number; startY?: number },
) => {
  const startTile = tiles[0];
  let lowX = startTile?.x;
  let highX = startTile?.x;
  let lowY = startTile?.y;
  let highY = startTile?.y;

  if (tiles.length > 1) {
    tiles.forEach((tile) => {
      if (tile.x < lowX) lowX = tile.x;
      else if (tile.x > highX) highX = tile.x;
      if (tile.y < lowY) lowY = tile.y;
      else if (tile.y > highY) highY = tile.y;
    });
  }

  const midX = (lowX + highX) / 2;
  const midY = (lowY + highY) / 2;
  const shiftX = startX - midX;
  const shiftY = startY - midY;

  return { shiftX, shiftY };
};

/**
 * Removes fields from an object
 */
const removeFields = (fields: string[], data: Record<string, any> = {}) =>
  Object.entries(data).reduce((acc, [key, value]) => {
    return fields.includes(key) ? acc : { ...acc, [key]: value };
  }, {});

/**
 * Removes any templateId and campaignId references from `nodeData`
 * Send tiles should not persist any campaign info in tilesets
 */
const removeCampaignDataFromNodeData = (
  nodeData: BackendWFNode['nodeData'] | Record<string, any> = {},
) => {
  const fields = ['templateId', 'campaignId'];
  const updatedNodeData = removeFields(fields, nodeData) as BackendWFNode['nodeData'];

  (updatedNodeData?.channels || []).forEach((channel: Channel) => {
    if (channel?.value) {
      channel.value = removeFields(fields, channel);
    }
  });
  return updatedNodeData;
};

interface HydrateTilesetArgs {
  /** The tileset to add into the journey */
  tilesetData: TilesetData;
  /** The next unsaved node id in the journey */
  nextNodeId: number;
  /** The x value where the tileset was dropped */
  startX?: number;
  /** The y value where the tileset was dropped */
  startY?: number;
  /** The start node id to replace */
  startNodeId?: number;
  /** Is tile generated with AI */
  generatedWithAI?: boolean;
}

interface NodeUpdateReducer {
  /**
   * Map of the tileset node ids to the node ids that the node will have in the
   * journey after it is added
   */
  nodeIdMap: Record<number, number>;
  /** The nodes in the tileset */
  nodes: BackendWFNode[];
  /** The id to use on the next added node */
  nextNodeId: number;
}

interface NoteUpdateReducer {
  /** The notes in the tileset */
  notes: BackendNoteNode[];
  /** The id to use on the next added node */
  nextNoteId: number;
}

/**
 * Parses the tileset data so that it can be added into the journey
 *
 * To accomplish this, it:
 * - Sets the node id to a negative number to show that it is an unsaved node
 * - Replaces the old node id in the edges with the new node id
 * - Removes any campaign data from the node data
 * - Shifts the nodes so that they are added where the tileset was dropped
 */
const updateDataToAddToJourney = (
  options: HydrateTilesetArgs,
): {
  nodes: BackendWFNode[];
  edges: BackendEdge[];
  notes: BackendNoteNode[];
  lastNodeId: number;
} => {
  const { startNodeId, tilesetData = {}, generatedWithAI = false } = options || {};

  const {
    nodes: tilesetNodes = [],
    notes: tilesetNotes = [],
    edges: tilesetEdges = [],
  } = tilesetData;
  // Calculate the shift in X and Y coordinates for the tileset
  const { shiftX, shiftY } = getTilesetShift([...tilesetNodes, ...tilesetNotes], options);

  // Update the nodes
  const { nodeIdMap, nodes, nextNodeId } = tilesetNodes.reduce(
    (acc: NodeUpdateReducer, node: BackendWFNode) => {
      // If a start tile is returned, save to the existing start tile
      const shouldReplaceStartTile =
        !!startNodeId && node.nodeData?.nodeSuperClass === WORKFLOW_NODE_TYPES.TRIGGER;
      // For non-Start tiles, change the id to a unique negative number as it is unsaved
      const newNodeId = shouldReplaceStartTile ? startNodeId : acc.nextNodeId;

      const updatedNode = {
        ...node,
        ...(generatedWithAI && { generatedWithAI: true }),
        id: newNodeId,
        nodeData: removeCampaignDataFromNodeData(node?.nodeData),
        // shift the node relative to the drop point
        x: node?.x + shiftX,
        y: node?.y + shiftY,
      };
      return {
        nextNodeId: acc.nextNodeId - 1,
        nodeIdMap: {
          ...acc.nodeIdMap,
          [node.id]: newNodeId,
        },
        nodes: [...acc.nodes, updatedNode],
      };
    },
    { nodeIdMap: {}, nodes: [], nextNodeId: options.nextNodeId },
  );

  // Update the edges
  const edges = tilesetEdges.map((edge: BackendEdge) => {
    // As the node ids have changed, we need to update the edge to point to the
    // new node ids
    const newSrcId = nodeIdMap[edge.srcWorkflowNodeId];
    const newDestId = nodeIdMap[edge.destWorkflowNodeId as number];
    return parseEdge({
      ...edge,
      srcWorkflowNodeId: newSrcId || edge.srcWorkflowNodeId,
      destWorkflowNodeId: newDestId || edge.destWorkflowNodeId,
    }) as BackendEdge;
  });

  // Update the notes
  const { notes, nextNoteId } = tilesetNotes.reduce(
    (acc: NoteUpdateReducer, note: BackendNoteNode) => {
      const updatedNote = {
        ...note,
        // change the id to a unique negative number as it is unsaved
        id: acc.nextNoteId,
        // shift the note relative to the drop point
        x: note?.x + shiftX,
        y: note?.y + shiftY,
      };
      return {
        nextNoteId: acc.nextNoteId - 1,
        notes: [...acc.notes, updatedNote],
      };
    },
    { notes: [], nextNoteId: nextNodeId },
  );

  return {
    notes,
    nodes,
    edges,
    lastNodeId: nextNoteId,
  };
};

/**
 * Parses the tileset data so that it can be added into a journey
 * @param options.tilesetData The tileset to add into the journey
 * @param options.nextNodeId The next unsaved node id in the journey
 * @param options.startX The x value where the tileset was dropped
 * @param options.startY The y value where the tileset was dropped
 * @param wfState The workflow state
 * @returns A set of correctly parsed nodes and edges to be added into the
 * journey, as well as the new `nextNodeId`
 */
export const hydrateTileset = (options: HydrateTilesetArgs, wfState: WorkflowState) => {
  // nodes/notes with the corrected ids and shifted to the correct position
  const { nodes = [], notes = [], edges = [], lastNodeId } = updateDataToAddToJourney(options);
  // key nodes by id
  const nodeMap: Record<NodeId, Node> = parseBackendNodes(
    { nodes, notes, shouldValidate: true },
    // TODO: check if we really want the notes to be visible even if other notes
    // are hidden.  It's likely that we instead want all notes to show when a
    // tileset is added
    { ...wfState, isNotesVisible: true },
  ).reduce((acc, node) => ({ ...acc, [node.id]: node }), {});

  return {
    edges: edges.map(parseEdge),
    nodes: nodeMap,
    nextNodeId: lastNodeId - 1,
  };
};

type DataForParsing = {
  nodes: Node[];
  edges: Edge[];
  notes?: NoteNode[];
};

/**
 * Parses selected nodes/edges for preview in the Create Tileset Modal
 */
export const parseForTilesetPreview = ({ nodes, edges }: DataForParsing) => {
  const parseNodesForTilesetPreview = (nodesForPreview: Node[] | NoteNode[]) => {
    return parseClonedNodes(nodesForPreview, true).map((node) => ({
      ...node,
      isSelected: false,
      canDelete: false,
    }));
  };

  const parseEdgesForTilesetPreview = (edgesForPreview: Edge[] = []): EdgeModel[] =>
    edgesForPreview.map(({ data }: any) => {
      const { source, sourceAnchor, target } = data;
      return { source, sourceAnchor, target, targetAnchor: 0 };
    });

  return {
    parsedNodesForDisplay: parseNodesForTilesetPreview(nodes),
    parsedEdgesForDisplay: parseEdgesForTilesetPreview(edges),
  };
};

/**
 * Parses selected nodes/edges for saving in the Create Tileset Modal
 */
export const parseForCreateTileset = ({ nodes, edges }: DataForParsing) => {
  const parsedNodesForSaving = parseClonedNodes(nodes, true);
  const { parsedNodes, parsedNotes } = parsedNodesForSaving?.reduce(
    (acc: { parsedNodes: WFNode[]; parsedNotes: NoteNode[] }, node) => {
      return node?.isNote
        ? { ...acc, parsedNotes: [...acc.parsedNotes, parseNodeForSaving(node)] }
        : { ...acc, parsedNodes: [...acc.parsedNodes, parseNodeForSaving(node)] };
    },
    { parsedNodes: [], parsedNotes: [] },
  );

  const parsedEdges: Edge[] = edges?.map(({ data: { source, target, sourceAnchor } }: any) => {
    return {
      srcWorkflowNodeId: parseInt(source, 10),
      destWorkflowNodeId: parseInt(target, 10),
      srcWorkflowNodeOutputIndex: sourceAnchor - 1,
    };
  });

  /**
   * The fields below are being omitted due not being present
   * in the GraphQL schema. Remove the field name listed below
   * when added to the backend schema.
   */
  return {
    parsedNodes: parsedNodes.map((node: BackendWFNode) => ({
      description: node.description,
      id: node.id,
      nodeData: node.nodeData,
      title: node.title,
      x: node.x,
      y: node.y,
    })),
    parsedNotes: parsedNotes.map((note: BackendNoteNode) => ({
      body: note.body,
      title: note.title,
      x: note.x,
      y: note.y,
    })),
    parsedEdges,
  };
};
